/**
 * One pay run: what it paid, what it withheld, the entry that books it, and what
 * can still be done with it (spec §18.6).
 *
 * THE ENTRY IS THE BOOKS' COPY, AND MAY DIFFER. A run's entry is an entry like
 * any other and can be edited in the editor, so this says when its lines no
 * longer book what the run calculated instead of implying they always do.
 *
 * VOIDED BEFORE POSTING, REVERSED AFTER. Only the one that applies is offered,
 * and each asks for a reason inline, the Matching screen's way: a stored reason
 * nobody chose is worse than none.
 *
 * WAITING FOR THE BANK DEBIT. A run prepared before payday holds a draft of our
 * own. When Mercury's debit arrives it is offered here, and choosing it moves
 * the run's lines onto the bank's transaction and deletes the draft, so the pay
 * is booked once (§18.6).
 */

import { AccessNotice } from '../components/AccessNotice.js';
import { useCan } from '../auth/AuthProvider.js';
import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Toast } from '../components/Toast.js';
import { ApiError, api, type PayrollRunDetail } from '../api.js';
import { periodWord } from '../lib/labels.js';
import { formatCents } from '../lib/money.js';
import { formatRateMillionths } from '../lib/rates.js';
import { formatTimestamp } from '../lib/timezone.js';
import { PayrollDeadlines } from '../components/PayrollDeadlines.js';

export function PayRunPage() {
  const mayRun = useCan()('run_payroll');
  const { id = '' } = useParams<{ id: string }>();
  const location = useLocation();
  const [run, setRun] = useState<PayrollRunDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ending, setEnding] = useState<{ kind: 'void' | 'reverse'; reason: string } | null>(null);

  const postRefusal = (location.state as { postRefusal?: string | null } | null)?.postRefusal ?? null;

  const load = useCallback(async () => {
    try {
      const result = await api.getPayrollRun(id);
      setRun(result.run);
      setLoadError(null);
    } catch (caught) {
      setLoadError(caught instanceof ApiError ? caught.message : 'Could not read the pay run.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function end() {
    if (ending === null || ending.reason.trim() === '') return;
    setBusy(true);
    setError(null);
    try {
      const result =
        ending.kind === 'void'
          ? await api.voidPayrollRun(id, ending.reason.trim())
          : await api.reversePayrollRun(id, ending.reason.trim());
      setRun(result.run);
      setEnding(null);
      setNotice(ending.kind === 'void' ? 'Pay run voided' : 'Pay run reversed');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not end the pay run.');
    } finally {
      setBusy(false);
    }
  }

  async function match(bankDebitHeaderId: string) {
    setBusy(true);
    setError(null);
    try {
      const matched = await api.matchPayrollRun(id, bankDebitHeaderId);
      setRun(matched.run);
      setNotice(matched.postRefusal ?? 'The bank debit now carries this run');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not use that bank debit.');
    } finally {
      setBusy(false);
    }
  }

  if (loadError) {
    return (
      <section className="payroll">
        <p>
          <Link to="/payroll">Back to Payroll</Link>
        </p>
        <p role="alert" className="error">
          {loadError}
        </p>
      </section>
    );
  }
  if (!run) return <p className="muted">Loading…</p>;

  const draft = run.entry?.status === 'draft';
  const posted = run.entry?.status === 'posted';

  return (
    <section className="payroll">
      <p>
        <Link to="/payroll">Back to Payroll</Link>
      </p>
      <h1>
        {run.employeeName}, Paid {run.payDate}
      </h1>
      <AccessNotice capability="run_payroll" what="pay runs" />
      {notice && <Toast message={notice} variant="ok" onDismiss={() => setNotice(null)} />}

      <p>
        <span className="tag">{runStatusLabel(run)}</span>{' '}
        {run.entry && (
          <span className="tag">
            {run.entry.source === 'mercury'
              ? 'Mercury Transaction'
              : run.entry.source === 'bank_feed'
                ? 'Imported Bank Transaction'
                : 'Manual Entry'}
          </span>
        )}
      </p>
      <p>
        <Link to={`/payroll/runs/${run.id}/statement`}>Earnings Statement</Link>
      </p>

      {postRefusal && <p className="notice notice-warn">{postRefusal}</p>}
      {run.entryMatchesRun === false && (
        <p className="notice notice-warn">
          This run's entry was changed after the run was made, so its lines no longer book what the run calculated. Void
          the run and make it again, or correct the entry in the editor.
        </p>
      )}
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      <section className="settings-section" aria-labelledby="pay-run-paid">
        <h2 id="pay-run-paid">What Was Paid</h2>
        <table className="lines" aria-label="What Was Paid">
          <tbody>
            <tr>
              <td>Gross Pay</td>
              <td className="amount">{formatCents(run.grossPayCents)}</td>
            </tr>
            {run.supplementalWagesCents > 0 && (
              <tr>
                <td className="muted">
                  Of which a bonus, withheld from{' '}
                  {run.supplementalMethod === 'flat' ? 'at the flat rate' : 'with regular pay'}
                </td>
                <td className="amount muted">{formatCents(run.supplementalWagesCents)}</td>
              </tr>
            )}
            <tr>
              <td>Federal Income Tax Withheld</td>
              <td className="amount">{formatCents(run.incomeTaxWithheldCents)}</td>
            </tr>
            <tr>
              <td>Social Security Withheld</td>
              <td className="amount">{formatCents(run.socialSecurityEmployeeCents)}</td>
            </tr>
            <tr>
              <td>Medicare Withheld</td>
              <td className="amount">{formatCents(run.medicareEmployeeCents)}</td>
            </tr>
            {run.additionalMedicareEmployeeCents > 0 && (
              <tr>
                <td>Additional Medicare Withheld</td>
                <td className="amount">{formatCents(run.additionalMedicareEmployeeCents)}</td>
              </tr>
            )}
            <tr>
              <td>
                <strong>Net Pay</strong>
              </td>
              <td className="amount">
                <strong>{formatCents(run.netPayCents)}</strong>
              </td>
            </tr>
          </tbody>
        </table>

        <table className="lines" aria-label="What the Business Owes">
          <tbody>
            <tr>
              <td>Social Security</td>
              <td className="amount">{formatCents(run.socialSecurityEmployerCents)}</td>
            </tr>
            <tr>
              <td>Medicare</td>
              <td className="amount">{formatCents(run.medicareEmployerCents)}</td>
            </tr>
            <tr>
              <td>FUTA</td>
              <td className="amount">{formatCents(run.futaEmployerCents)}</td>
            </tr>
            <tr>
              <td>Texas Unemployment at {formatRateMillionths(run.texasUnemploymentRateMillionths)}</td>
              <td className="amount">{formatCents(run.texasUnemploymentEmployerCents)}</td>
            </tr>
            <tr>
              <td>
                <strong>Payroll Taxes</strong>
              </td>
              <td className="amount">
                <strong>{formatCents(run.employerTaxesCents)}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <section className="settings-section" aria-labelledby="pay-run-counted">
        <h2 id="pay-run-counted">What It Counted</h2>
        <dl className="record-facts">
          <dt>Pay Rate</dt>
          <dd>
            {formatCents(run.payRate.amountPerPeriodCents)} each {periodWord(run.payRate.frequency)}, since{' '}
            {run.payRate.effectiveOn}
          </dd>
          <dt>W-4</dt>
          <dd>
            Form W-4 ({run.w4.formYear}), taking effect {run.w4.effectiveOn}
          </dd>
          <dt>Income Tax Wages</dt>
          <dd>
            {formatCents(run.incomeTaxWagesCents)}
            {run.healthPremiumCents > 0 && `, including ${formatCents(run.healthPremiumCents)} of health insurance`}
          </dd>
          <dt>Wages Earlier in {run.taxYear}</dt>
          <dd>
            {formatCents(run.yearToDate.socialSecurityWagesCents)} for Social Security ·{' '}
            {formatCents(run.yearToDate.futaWagesCents)} for FUTA ·{' '}
            {formatCents(run.yearToDate.texasUnemploymentWagesCents)} for Texas unemployment
          </dd>
          <dt>Entry</dt>
          <dd>
            {run.entry ? (
              <Link to={`/journals/${run.entry.headerId}`}>Open the Entry</Link>
            ) : (
              'Deleted with the void'
            )}
          </dd>
          {run.reversalHeaderId && (
            <>
              <dt>Reversal</dt>
              <dd>
                <Link to={`/journals/${run.reversalHeaderId}`}>Open the Reversing Entry</Link>
              </dd>
            </>
          )}
        </dl>
      </section>

      {/*
        After what the run counted and before the bank side: the deposit is a
        consequence of the figures above it, and it is the next thing the
        operator has to act on once the pay itself is dealt with.

        Absent once the run is voided or reversed: it owes nothing, and a date
        beside it would name a day nothing is due on. The server sends null.
      */}
      {run.deadlines && <PayrollDeadlines payDate={run.payDate} deadlines={run.deadlines} />}

      {run.awaitingBankDebit && (
        <section className="settings-section" aria-labelledby="pay-run-bank">
          <h2 id="pay-run-bank">Waiting for the Bank Debit</h2>
          <p className="muted">
            This run holds a draft of our own. When Mercury's debit of {formatCents(run.netPayCents)} arrives, choose it
            here: its transaction takes the run's lines and the draft is deleted, so the pay is booked once.
          </p>
          {run.bankDebits.length === 0 ? (
            <p className="muted">No Mercury debit of {formatCents(run.netPayCents)} has arrived within a week of {run.payDate}.</p>
          ) : (
            <ul className="history-list" aria-label="Bank Debits">
              {run.bankDebits.map((debit) => (
                <li key={debit.headerId}>
                  <p>
                    {debit.entryDate} · {debit.counterparty ?? debit.bankMemo ?? 'no description'} ·{' '}
                    {formatCents(Math.abs(debit.amountCents))} · {debit.accountCode} {debit.accountName}
                  </p>
                  {mayRun && (
                    <button type="button" disabled={busy} onClick={() => void match(debit.headerId)}>
                      Use This Debit
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {mayRun && run.status === 'active' && (
        <div className="settings-section">
          {ending === null ? (
            <button
              type="button"
              className="link"
              onClick={() => setEnding({ kind: posted ? 'reverse' : 'void', reason: '' })}
            >
              {posted ? 'Reverse This Run' : 'Void This Run'}
            </button>
          ) : (
            <div className="new-account undo-form" role="group" aria-label={ending.kind === 'void' ? 'Void This Run' : 'Reverse This Run'}>
              <p>
                {ending.kind === 'void'
                  ? draft
                    ? "The entry's lines are cleared, and a draft of our own is deleted. The run is kept, marked voided, and counts towards nothing."
                    : 'The run is kept, marked voided, and counts towards nothing.'
                  : "A reversing entry dated today undoes the posted one. Both are kept, and the run counts towards nothing."}
              </p>
              <label className="field">
                <span>Why?</span>
                <input
                  aria-label="Reason"
                  value={ending.reason}
                  onChange={(event) => setEnding({ ...ending, reason: event.target.value })}
                />
              </label>
              <div className="row">
                <button type="button" disabled={busy || ending.reason.trim() === ''} onClick={() => void end()}>
                  {ending.kind === 'void' ? 'Void It' : 'Reverse It'}
                </button>
                <button type="button" className="link" onClick={() => setEnding(null)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {run.status !== 'active' && (
        <p className="muted">
          {runStatusLabel(run)} {run.voidedAt ? formatTimestamp(run.voidedAt) : ''} by{' '}
          {run.voidedByEmail ?? 'no user recorded'}: {run.voidReason}
        </p>
      )}
    </section>
  );
}

/** What the operator calls a run's state, with the entry's state where it matters. */
export function runStatusLabel(run: { status: string; entry: { status: string } | null }): string {
  if (run.status === 'voided') return 'Voided';
  if (run.status === 'reversed') return 'Reversed';
  if (run.entry?.status === 'posted') return 'Posted';
  if (run.entry?.status === 'dismissed') return 'Entry Excluded';
  return 'Draft Entry';
}
